/**
 * Voxly — PostMetaStrip.
 *
 * Renders the tap-driven metadata attached to a post (Phase 72):
 *   📍 Brooklyn · feeling grateful · with @sara @jay · #sunday #coffee
 *
 * Empty when none of the four fields are set, so it can be unconditionally
 * mounted from PostCard without an outer guard. Hashtags and tagged users
 * are interactive (route to /h/:tag and /u/:user_id).
 */
import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Smiley, At, Hash } from "@phosphor-icons/react";

// Mirror the curated list from MetadataComposer so we can render the
// matching emoji next to the feeling word.
const FEELING_EMOJI = {
  happy: "😄", grateful: "🙏", excited: "🤩", calm: "😌",
  "in love": "🥰", nostalgic: "🕰️", energized: "⚡️",
  proud: "🦁", tired: "🥱", sad: "🥲", frustrated: "😤", curious: "🤔",
};

export default function PostMetaStrip({ post }) {
  const tagged = post.tagged_users || [];
  const hashtags = post.hashtags || [];
  const location = post.location;
  const feeling = post.feeling;
  if (!tagged.length && !hashtags.length && !location && !feeling) return null;

  return (
    <div
      className="px-4 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground"
      data-testid={`post-meta-${post.post_id}`}
    >
      {location?.label && (
        <span className="inline-flex items-center gap-1 text-foreground/85" data-testid={`post-meta-location-${post.post_id}`}>
          <MapPin size={12} weight="fill" className="text-primary" />
          <span className="font-semibold">{location.label}</span>
        </span>
      )}
      {feeling && (
        <span className="inline-flex items-center gap-1 text-foreground/85" data-testid={`post-meta-feeling-${post.post_id}`}>
          <Smiley size={12} weight="fill" className="text-primary" />
          <span className="font-semibold">
            feeling <span className="lowercase">{feeling}</span> {FEELING_EMOJI[feeling] || ""}
          </span>
        </span>
      )}
      {tagged.length > 0 && (
        <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`post-meta-tagged-${post.post_id}`}>
          <At size={12} weight="bold" className="text-primary" />
          <span className="font-semibold text-foreground/85">with</span>
          {tagged.slice(0, 4).map((u, i) => (
            <React.Fragment key={u.user_id}>
              <Link
                to={`/u/${u.user_id}`}
                className="text-primary hover:underline font-semibold"
                data-testid={`post-meta-tag-${u.user_id}`}
              >
                {u.username ? `@${u.username}` : u.name?.split(" ")[0]}
              </Link>
              {i < Math.min(tagged.length, 4) - 1 && <span className="text-foreground/60">,</span>}
            </React.Fragment>
          ))}
          {tagged.length > 4 && (
            <span className="text-foreground/60">+{tagged.length - 4}</span>
          )}
        </span>
      )}
      {hashtags.length > 0 && (
        <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`post-meta-tags-${post.post_id}`}>
          <Hash size={12} weight="bold" className="text-primary" />
          {hashtags.slice(0, 6).map((t) => (
            <Link
              key={t}
              to={`/h/${encodeURIComponent(t)}`}
              data-testid={`post-meta-hashtag-${t}`}
              className="text-primary hover:underline font-semibold"
            >
              #{t}
            </Link>
          ))}
          {hashtags.length > 6 && (
            <span className="text-foreground/60">+{hashtags.length - 6}</span>
          )}
        </span>
      )}
    </div>
  );
}
